import React, { useState, useEffect } from 'react';
import ConfirmationModal from './ConfirmationModal';
import { fetchWithAuth } from '../util/fetchWithAuth';
import Modal from 'react-modal';
import styles from './UpdatePickUpPointModal.module.css';
import { toast } from 'react-toastify';
import { PickUpPoint } from '../types';

const REASONS: { key: string; label: string; applicableStatuses: string[] }[] = [
  { key: 'does_not_meet_criteria', label: 'Does not meet pickup point criteria', applicableStatuses: ['submitted', 'paid', 'items_purchased'] },
  { key: 'waiting_too_long', label: 'Was waiting at pick up point for too long', applicableStatuses: ['ready_for_collection'] },
];

interface UpdatePickUpPointModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  orderId: number;
  currentPickUpPointLocation: string;
  orderStatus: string;
}

const UpdatePickUpPointModal: React.FC<UpdatePickUpPointModalProps> = ({ isOpen, onClose, onSuccess, orderId, currentPickUpPointLocation, orderStatus }) => {
  const availableReasons = REASONS.filter((r) => r.applicableStatuses.includes(orderStatus));
  const [selectedReasonKey, setSelectedReasonKey] = useState('');
  const selectedReasonLabel = REASONS.find((r) => r.key === selectedReasonKey)?.label ?? '';
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showNotificationConfirmModal, setShowNotificationConfirmModal] = useState(false);
  const [defaultPickUpPointName, setDefaultPickUpPointName] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchWithAuth('/pick_up_points?per_page=100')
        .then(res => res.json())
        .then(data => {
          const defaultPoint = (data.pick_up_points || []).find((p: PickUpPoint) => p.default);
          if (defaultPoint) {
            setDefaultPickUpPointName(defaultPoint.city ? `${defaultPoint.name}, ${defaultPoint.city}` : defaultPoint.name);
          }
        })
        .catch(() => {});
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!selectedReasonKey) {
      toast.error('Please select a reason.');
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    setShowConfirmModal(false);
    setShowNotificationConfirmModal(true);
  };

  const handleFinalConfirm = async (sendNotification: boolean) => {
    setShowNotificationConfirmModal(false);
    try {
      const response = await fetchWithAuth(`/orders/${orderId}/update_pick_up_point`, {
        method: 'PATCH',
        body: JSON.stringify({ reason: selectedReasonKey, send_notification: sendNotification }),
      });
      if (!response.ok) {
        const data = await response.json();
        toast.error(data.errors?.[0] || 'Failed to update pick-up point.');
        return;
      }
      onSuccess();
      onCloseModal();
      if (orderStatus === 'submitted') {
        // Unpaid, so the fee came off the total instead of being credited -- any invoice
        // already sent still bills them for it.
        toast.success('Pick-up point updated. Issue the customer an updated invoice — the collection fee has been removed.', { autoClose: 10000 });
      } else {
        toast.success('Pick-up point updated to default location.');
      }
    } catch (error) {
      toast.error('Failed to update pick-up point. Please try again.');
    }
  };

  const onCloseModal = () => {
    setSelectedReasonKey('');
    setShowConfirmModal(false);
    setShowNotificationConfirmModal(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} onRequestClose={onCloseModal} className={styles.modalContent}>
        <h2 className="text-2xl font-bold mb-4">Change Pick-Up Point</h2>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            This will move the order from <strong>{currentPickUpPointLocation}</strong> to <strong>{defaultPickUpPointName || 'the default pick-up point'}</strong>.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Reason for change</label>
            {availableReasons.map((reason) => (
              <label key={reason.key} className="flex items-center gap-2 mb-2 cursor-pointer">
                <input
                  type="radio"
                  name="reason"
                  value={reason.key}
                  checked={selectedReasonKey === reason.key}
                  onChange={(e) => setSelectedReasonKey(e.target.value)}
                  className="h-4 w-4"
                />
                <span className="text-sm text-gray-700">{reason.label}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end space-x-2">
            <button
              onClick={onCloseModal}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50"
              disabled={!selectedReasonKey}
            >
              Change Pick-Up Point
            </button>
          </div>
        </div>
      </Modal>

      {showConfirmModal && (
        <ConfirmationModal
          isOpen={showConfirmModal}
          message={`Are you sure you want to move this order to ${defaultPickUpPointName || 'the default pick-up point'}? Reason: "${selectedReasonLabel}"`}
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}

      {showNotificationConfirmModal && (
        <ConfirmationModal
          isOpen={showNotificationConfirmModal}
          message="Would you like to send a notification?"
          onConfirm={() => handleFinalConfirm(true)}
          onCancel={() => handleFinalConfirm(false)}
        />
      )}
    </>
  );
};

export default UpdatePickUpPointModal;
